import {
  createRouter as _createRouter,
  createWebHistory,
  type RouteLocationRaw,
  type Router,
  RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router'

import { setCanonicalLink } from '@/lib/utils/browserHelpers'
import routeUtils from '@/lib/utils/routes'

import dynamicRoutes, { DynamicRoute } from '@/router/dynamic'
import routes from '@/router/routes'

import { getProductCrumbs } from '@/components/common/contentful/helpers/contentfulHelpers'

import app from '@/app'
import { useAppStore } from '@/stores/app'
import { useBreadcrumbsStore } from '@/stores/breadcrumbs'
import { useSessionStore } from '@/stores/session'
import { ContentfulPageWithCrumbs, ComponentModule, ContentfulProductMeta } from '@/types'

export function createRouter() {
  return _createRouter({ routes, history: createWebHistory() })
}

export function initRouter({
  router,
  emitter,
  ContentfulService,
}: {
  router: Router
  emitter: typeof app.config.globalProperties.$emitter
  ContentfulService: typeof app.config.globalProperties.$contentful
}) {
  let isEnabled = true

  /**
   * Add css class to body with a name of the current route.
   */
  router.afterEach((to, from) => {
    const appStore = useAppStore()
    const breadcrumbsStore = useBreadcrumbsStore()

    const isGoToSameRoute = to.fullPath === from.fullPath
    if (!(isGoToSameRoute && to.name === 'redirect')) {
      document.body.classList.remove('overflow-hidden')
      if (from.meta.className) document.body.classList.remove(from.meta.className)
      if (to.meta.className) document.body.classList.add(to.meta.className)
    }
    if (isGoToSameRoute) return
    if (!(to.redirectedFrom && to.name === 'authentication')) appStore.setPreviousRoute(from)
    breadcrumbsStore.setPath(to)
    if (to.name !== 'search') appStore.setHeaderSearchQuery('')
    emitter.emit('route.after', { to, from })
    setCanonicalLink()
  })

  router.beforeEach((to, from, next) => {
    const appStore = useAppStore()
    const sessionStore = useSessionStore()
    if (!isEnabled) {
      return
    }

    const redirect = ContentfulService.service.getRedirect(to.path)
    if (redirect) {
      if (redirect.includes('http')) {
        window.location.href = redirect
      } else {
        next(redirect)
      }
      return
    }

    const isFullPathUrl = to.fullPath.includes('://')
    const isContainsDoubleSlashes = to.fullPath.includes('//')
    if (!isFullPathUrl && isContainsDoubleSlashes) {
      next({
        name: undefined,
        path: to.fullPath.replaceAll('//', '/'),
        params: to.params,
        query: to.query,
      } as RouteLocationRaw)
      return
    }

    if (!appStore.isInitialized) {
      const unsubscribe = appStore.$subscribe((mutation, state) => {
        if (state.isInitialized) {
          unsubscribe()
          checkRoute()
        }
      })
    } else {
      checkRoute()
    }

    async function checkRoute() {
      // AUTHENTICATION HANDLING
      if (handleAuthenticationChecks()) return

      // HANDLE REDIRECT ROUTE
      if (to.name === 'redirect') {
        const { rootSlug, endSlug } = extractSlugsFromPath(to.params.pathMatch)

        // Try to resolve content from Contentful
        const cPage = ContentfulService.service.getPageBySlug(endSlug)
        const cProduct = !cPage ? ContentfulService.service.getProductMetaBySlug(rootSlug) : null
        const slugPath = determineSlugPath(rootSlug, endSlug, cPage)

        // CASE 1: CONTENTFUL PAGE FOUND
        if (cPage && cPage.fields) {
          return handleContentfulPage(cPage, slugPath, from)
        }
        // CASE 2: PRODUCT FOUND
        else if (cProduct) {
          return handleProductPage(cProduct, rootSlug)
        }
        // CASE 3: NO MATCHING CONTENT
        else {
          return handleNotFoundPage(to, from)
        }
      }
      // DEFAULT CASE: STANDARD ROUTE
      else {
        next()
      }

      // HELPER FUNCTIONS
      function handleAuthenticationChecks() {
        if (sessionStore.isUserLoggedIn && to.matched[0]?.meta?.logoutRoute) {
          handleLogout()
          return true
        } else if (sessionStore.isUserLoggedIn && to.matched[0].meta.guestRoute) {
          next('/')
          return true
        } else if (!sessionStore.isUserLoggedIn && to.matched[0]?.meta?.privateRoute) {
          appStore.setPreviousRoute(to)
          next({ name: 'authentication' })
          return true
        }
        return false
      }

      async function handleLogout() {
        appStore.setPreviousRoute(from)
        await sessionStore.logout()
        next('/authentication')
      }

      function extractSlugsFromPath(pathMatch: string[] | string) {
        return {
          rootSlug: routeUtils.getSlugFromPathMatch(pathMatch, 0),
          endSlug: routeUtils.getSlugFromPathMatch(pathMatch),
        }
      }

      function determineSlugPath(
        rootSlug: string,
        endSlug: string,
        cPage?: ContentfulPageWithCrumbs,
      ) {
        return cPage ? cPage.slug : rootSlug === endSlug ? endSlug : `${rootSlug}/${endSlug}`
      }

      async function handleContentfulPage(
        cPage: ContentfulPageWithCrumbs,
        slugPath: string,
        from: RouteLocationNormalized,
      ) {
        // Handle educational content redirect
        if (ContentfulService.service.isEducationalContent(cPage.fields?.pageType)) {
          next(cPage.link)
          return
        }

        // Map crumbs
        const crumbs = cPage.crumbs.map((el) => ({
          name: el.fields?.title,
          link: el.fields?.slug,
        }))

        // Find matching route for page type
        const route = findRouteForPageType(cPage)

        // Apply route configuration if found
        if (route && 'component' in route) {
          await applyRouteToDestination(route, cPage, crumbs, from)
          completeNavigation(slugPath)
        } else {
          next()
        }
      }

      function findRouteForPageType(cPage: ContentfulPageWithCrumbs) {
        let matchedRoute: DynamicRoute | null = null

        Object.entries(dynamicRoutes).forEach(([routeKey, routeData]: [string, DynamicRoute]) => {
          if (isPageTypeMatch(routeData, cPage)) {
            matchedRoute = routeData

            // Special case for category without subpages
            if (routeKey === 'category' && !cPage.fields.subpages?.length) {
              matchedRoute = dynamicRoutes.subcategory
            }

            // Set redirect flag for certain page types
            const afterLoginRedirectPageTypes = ['catalog', 'category', 'subcategory']
            if (afterLoginRedirectPageTypes.includes(routeKey)) {
              to.meta.isAfterLoginRedirectToPrevRoute = true
            }
          }
        })

        return matchedRoute
      }

      function isPageTypeMatch(routeData: DynamicRoute, cPage: ContentfulPageWithCrumbs) {
        return (
          'pageTypes' in routeData &&
          !!routeData.pageTypes &&
          !!cPage &&
          routeData.pageTypes.includes(cPage.fields.pageType as string)
        )
      }

      async function applyRouteToDestination(
        route: DynamicRoute,
        cPage: ContentfulPageWithCrumbs,
        crumbs: Array<{ name: string; link: string }>,
        from: RouteLocationNormalized,
      ) {
        // Set component
        if (to.matched?.[0]?.components) {
          const moduleResult = await route.component()
          const componentModule = moduleResult as ComponentModule
          to.matched[0].components.default = componentModule.default
        }

        // Set props
        if (to.matched?.[0]?.props) {
          to.matched[0].props.default = {
            cPage: cPage,
            catalog: cPage.crumbs[0]?.fields || {},
          }
        }

        // Set metadata
        to.meta.className = route.className
        to.meta.crumbs = crumbs
        to.meta.isScrollingUp = !(
          from.meta.className === to.meta.className && to.meta.className === 'catalog-page'
        )
      }

      async function handleProductPage(cProduct: ContentfulProductMeta, rootSlug: string) {
        const crumbs = getProductCrumbs(cProduct)
        const route = dynamicRoutes.product

        // Set component
        if (to.matched?.[0]?.components) {
          const moduleResult = await route.component()
          const componentModule = moduleResult as ComponentModule
          to.matched[0].components.default = componentModule.default
        }

        // Set metadata
        to.meta.className = route.className
        to.meta.crumbs = [...crumbs, { name: cProduct.fields.title, link: cProduct.fields.slug }]
        to.params.key = rootSlug
        to.meta.isAfterLoginRedirectToPrevRoute = true

        next()
      }

      function handleNotFoundPage(to: RouteLocationNormalized, from: RouteLocationNormalized) {
        if (to.path !== from.path) {
          createDynamicNotFoundRoute(to)
          next({ name: to.path })
        }
        return
      }

      function createDynamicNotFoundRoute(to: RouteLocationNormalized) {
        const routesList = router.getRoutes()
        const notFoundRoute = routesList.find((route) => route.name === 'not-found')
        const dynamicallyCreatedRoute = routesList.find((route) => route.name === to.path)

        if (!dynamicallyCreatedRoute && notFoundRoute) {
          const newPageRoute = { ...notFoundRoute } as RouteRecordRaw
          newPageRoute.path = to.path
          newPageRoute.name = to.path
          router.addRoute(newPageRoute)
        }
      }

      function completeNavigation(slugPath: string) {
        next()
        const hash = to.hash || ''
        const newLocation = '/' + slugPath + hash + location.search
        history.replaceState(history.state, '', newLocation)
      }
    }
  })

  emitter.on('contentful.rich:bio-link', (aNode) => {
    if (aNode instanceof HTMLAnchorElement) {
      const link = aNode.pathname + aNode.search
      router.push(link)
    }
  })

  /**
   * Disable router.
   */
  emitter.on('router.stop', () => {
    isEnabled = false
  })

  /**
   * Enable router.
   */
  emitter.on('router.start', () => {
    isEnabled = true
  })
}
